/*
  Summit Saved (wishlist): saved products kept in the shopper's browser (localStorage), no login.

  - <wishlist-button handle="...">  save/unsave toggle on product cards, the product page and panel rows
  - <wishlist-count>                fills the header bookmark while anything is saved
  - <saved-drawer-body>          the slide-out panel (snippets/saved-drawer.liquid), filled on first open
  - <wishlist-grid>                 the Saved page (/pages/saved)

  Both the panel and the page render each saved product from sections/wishlist-card.liquid, fetched
  on the product's own URL, so cards and prices always match the rest of the site.

  Saving tells Klaviyo ("Saved Product") so flows can follow up. Klaviyo only records it for
  people it knows: anyone who has clicked a Summit email, and signed-in customers, who are
  identified here by email first. Anyone else can leave their email in the panel's alerts box,
  which subscribes them, identifies them and re-sends everything they have saved.

  Storage can be missing or throw (private windows, blocked site data); the list then lives for
  the page view only and nothing breaks.
*/

const STORAGE_KEY = 'summit-wishlist';
const MAX_ITEMS = 100;
const CHANGE_EVENT = 'wishlist:change';

let memoryItems = [];

function read() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item) => item && typeof item.h === 'string') : [];
  } catch (error) {
    return memoryItems;
  }
}

function write(items) {
  memoryItems = items.slice(0, MAX_ITEMS);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryItems));
  } catch (error) {
    // Storage unavailable: keep the in-memory copy for this page view.
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: memoryItems } }));
}

export const wishlist = {
  items: () => read(),
  handles: () => read().map((item) => item.h),
  has: (handle) => read().some((item) => item.h === handle),
  add(handle) {
    if (!handle || this.has(handle)) return;
    write([{ h: handle, t: Date.now() }, ...read()]);
  },
  remove(handle) {
    write(read().filter((item) => item.h !== handle));
  },
  rename(oldHandle, newHandle) {
    if (oldHandle === newHandle) return;
    const items = read();
    const hasNew = items.some((item) => item.h === newHandle);
    write(hasNew ? items.filter((item) => item.h !== oldHandle) : items.map((item) => (item.h === oldHandle ? { ...item, h: newHandle } : item)));
  },
};

window.SummitWishlist = wishlist;

/* ---------- product fetch (shared by the panel and the page) ---------- */

const productCache = new Map();

// Resolves to the parsed wishlist-card section for a handle, or null if the product is gone.
function fetchProduct(handle) {
  if (!productCache.has(handle)) {
    const root = window.Shopify?.routes?.root || '/';
    const request = fetch(`${root}products/${encodeURIComponent(handle)}?section_id=wishlist-card`)
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const card = doc.querySelector('product-card');
        if (!card) return null;

        // A product whose address changed redirects to its new handle: follow it in storage too.
        const actualHandle = card.getAttribute('handle');
        if (actualHandle && actualHandle !== handle) {
          productCache.set(actualHandle, Promise.resolve(doc));
          wishlist.rename(handle, actualHandle);
        }
        return doc;
      })
      .catch(() => null);
    productCache.set(handle, request);
  }
  return productCache.get(handle);
}

/* ---------- tracking ---------- */

let identified = false;

function track(button) {
  const data = button.dataset;
  const properties = {
    ProductID: data.productId,
    ProductName: data.title,
    Handle: button.getAttribute('handle'),
    URL: data.url,
    ImageURL: data.image,
    Price: Number(data.price),
    CompareAtPrice: Number(data.compareAtPrice) || undefined,
    Available: data.available === 'true',
  };

  try {
    const klaviyo = window.klaviyo || (window.klaviyo = []);
    const customer = window.SummitSavedCustomer;

    if (customer?.email && !identified) {
      identified = true;
      typeof klaviyo.identify === 'function' ? klaviyo.identify({ email: customer.email }) : klaviyo.push(['identify', { email: customer.email }]);
    }

    typeof klaviyo.track === 'function' ? klaviyo.track('Saved Product', properties) : klaviyo.push(['track', 'Saved Product', properties]);
  } catch (error) {}

  try {
    window.Shopify?.analytics?.publish?.('product_saved', properties);
  } catch (error) {}
}

/* ---------- alerts sign-up (signed-out shoppers, in the panel) ---------- */

const KLAVIYO_COMPANY = 'VhpPJT';
const KLAVIYO_LIST = 'YwRiAJ'; // Shopify Marketing
const ALERTS_KEY = 'summit-saved-alerts';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function alertsSignedUp() {
  try {
    return window.localStorage.getItem(ALERTS_KEY) === '1';
  } catch (error) {
    return false;
  }
}

function subscribe(email) {
  const body = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Saved panel',
        profile: { data: { type: 'profile', attributes: { email } } },
      },
      relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST } } },
    },
  };

  return fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${KLAVIYO_COMPANY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
    body: JSON.stringify(body),
  }).then((response) => {
    if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
  });
}

// Tells Klaviyo who this browser is, then re-sends every saved product so the flows have them.
async function identifyAndResend(email) {
  const klaviyo = window.klaviyo || (window.klaviyo = []);
  identified = true;
  try {
    // klaviyo.js never settles while the visitor has declined cookies, so don't wait on it forever.
    if (typeof klaviyo.identify === 'function') await Promise.race([klaviyo.identify({ email }), new Promise((resolve) => setTimeout(resolve, 3000))]);
    else klaviyo.push(['identify', { email }]);
  } catch (error) {}

  const docs = await Promise.all(wishlist.handles().map((handle) => fetchProduct(handle)));
  docs.forEach((doc) => {
    const button = doc?.querySelector('wishlist-button');
    if (button) track(button);
  });
}

/* ---------- toast ---------- */

let toast;
let toastTimer;

function showToast(message, withLink) {
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.setAttribute('role', 'status');
    document.body.append(toast);

    toast.addEventListener('click', (event) => {
      if (!event.target.closest('[data-saved-open]')) return;
      const drawer = document.getElementById('saved-drawer');
      if (drawer?.show) {
        event.preventDefault();
        toast.classList.remove('is-visible');
        drawer.show();
      }
    });
  }

  const url = document.querySelector('[data-saved-view]')?.getAttribute('href') || '/pages/saved';
  toast.innerHTML = `<span>${message}</span>${withLink ? `<a href="${url}" data-saved-open>View</a>` : ''}`;
  toast.classList.add('is-visible');

  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2800);
}

/* ---------- elements ---------- */

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('button');
    this.onClick = this.onClick.bind(this);
    this.sync = this.sync.bind(this);
    this.toggle?.addEventListener('click', this.onClick);
    document.addEventListener(CHANGE_EVENT, this.sync);
    this.sync();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onClick);
    document.removeEventListener(CHANGE_EVENT, this.sync);
  }

  get handle() {
    return this.getAttribute('handle');
  }

  sync() {
    const saved = wishlist.has(this.handle);
    this.classList.toggle('is-saved', saved);
    this.toggle?.setAttribute('aria-pressed', String(saved));
    const title = this.dataset.title || 'this product';
    this.toggle?.setAttribute('aria-label', saved ? `Remove ${title} from Saved` : `Save ${title}`);
  }

  onClick(event) {
    event.preventDefault();
    event.stopPropagation();

    // Inside the panel or on the Saved page the item visibly leaves; no toast needed there.
    const inList = Boolean(this.closest('wishlist-grid, saved-drawer-body'));

    if (wishlist.has(this.handle)) {
      wishlist.remove(this.handle);
      if (!inList) showToast('Removed from Saved', false);
    } else {
      wishlist.add(this.handle);
      this.classList.remove('is-popping');
      void this.offsetWidth;
      this.classList.add('is-popping');
      track(this);
      if (!inList) showToast('Saved', true);
    }
  }
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.sync = this.sync.bind(this);
    document.addEventListener(CHANGE_EVENT, this.sync);
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.sync);
  }

  sync() {
    const count = wishlist.items().length;
    // No dot (the bag has one): the bookmark itself fills in once anything is saved.
    this.closest('a')?.classList.toggle('has-saved', count > 0);
    this.closest('a')?.setAttribute('aria-label', count > 0 ? `Saved (${count})` : 'Saved');
  }
}

function countLabel(count) {
  return count === 1 ? '1 item' : `${count} items`;
}

// Shared by the panel and the page: keeps a container's children in step with the saved list.
class SavedList extends HTMLElement {
  connectedCallback() {
    this.onChange = this.onChange.bind(this);
    document.addEventListener(CHANGE_EVENT, this.onChange);
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.onChange);
  }

  // Override: turn a fetched section document into the element to show.
  pick() {
    return null;
  }

  async render() {
    const handles = wishlist.handles();
    if (handles.length === 0) {
      this.list.replaceChildren();
      this.updateState(0);
      this.classList.add('is-ready');
      return;
    }

    this.rendering = true;
    try {
      const docs = await Promise.all(handles.map((handle) => fetchProduct(handle)));
      const fragment = document.createDocumentFragment();
      docs.forEach((doc) => {
        const element = doc && this.pick(doc);
        if (element) fragment.append(document.importNode(element, true));
      });
      this.list.replaceChildren(fragment);
    } finally {
      this.rendering = false;
    }
    this.updateState(this.list.children.length);
    this.classList.add('is-ready');
  }

  onChange() {
    if (this.rendering || !this.rendered) return;
    const handles = wishlist.handles();
    const items = Array.from(this.list.children);

    items.forEach((item) => {
      if (!handles.includes(item.getAttribute('handle'))) {
        item.classList.add('is-removing');
        setTimeout(() => {
          item.remove();
          this.updateState(this.list.children.length);
        }, 250);
      }
    });

    // Saved somewhere else (another tab, a card behind the panel): re-render to pick it up.
    const shown = items.map((item) => item.getAttribute('handle'));
    if (handles.some((handle) => !shown.includes(handle))) this.render();
  }

  updateState(count) {
    if (this.empty) this.empty.hidden = count > 0;
    this.list.hidden = count === 0;
    this.countLabels.forEach((label) => (label.textContent = count > 0 ? countLabel(count) : ''));
  }
}

class WishlistGrid extends SavedList {
  connectedCallback() {
    super.connectedCallback();
    this.list = this.querySelector('[data-wishlist-list]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.countLabels = Array.from(this.querySelectorAll('[data-wishlist-count]'));
    this.rendered = true;
    this.render();
  }

  pick(doc) {
    return doc.querySelector('product-card');
  }
}

class SavedDrawerBody extends SavedList {
  connectedCallback() {
    super.connectedCallback();
    this.drawer = this.closest('x-drawer');
    this.list = this.querySelector('[data-saved-rows]');
    this.empty = this.querySelector('[data-saved-empty]');
    this.countLabels = this.drawer ? Array.from(this.drawer.querySelectorAll('[data-saved-count]')) : [];
    this.viewButton = this.drawer?.querySelector('[data-saved-view]');
    this.alertsForm = this.drawer?.querySelector('[data-saved-alerts]');
    this.alertsOff = alertsSignedUp();
    this.alertsForm?.addEventListener('submit', this.onAlertsSubmit.bind(this));

    // "Sign in" opens Shopify's own sign-in popup in place where the header account icon is
    // showing (tablet and up). On phones that icon is hidden, so the link's normal page is used.
    this.drawer?.querySelector('[data-saved-signin]')?.addEventListener('click', (event) => {
      const account = document.querySelector('.header__account-link shopify-account');
      if (!account || account.offsetParent === null || typeof account.showModal !== 'function') return;
      event.preventDefault();
      this.drawer.hide?.();
      setTimeout(() => account.showModal(), 320);
    });

    // Nothing is fetched until the panel is first opened.
    this.drawer?.addEventListener('dialog:before-show', () => {
      toast?.classList.remove('is-visible');
      if (!this.rendered) {
        this.rendered = true;
        this.checkKnown();
        this.render();
      }
    });
  }

  pick(doc) {
    return doc.querySelector('template[data-saved-row]')?.content.firstElementChild || null;
  }

  updateState(count) {
    super.updateState(count);
    if (this.viewButton) this.viewButton.hidden = count === 0;
    if (this.alertsForm && !this.alertsForm.classList.contains('is-done')) this.alertsForm.hidden = this.alertsOff || count === 0;
  }

  // Someone Klaviyo already knows (e.g. arrived from an email) is tracked already: no box for them.
  checkKnown() {
    if (!this.alertsForm || this.alertsOff) return;
    try {
      window.klaviyo?.isIdentified?.()?.then?.((known) => {
        if (!known) return;
        this.alertsOff = true;
        if (!this.alertsForm.classList.contains('is-done')) this.alertsForm.hidden = true;
      });
    } catch (error) {}
  }

  async onAlertsSubmit(event) {
    event.preventDefault();
    const form = this.alertsForm;
    const message = form.querySelector('[data-saved-alerts-msg]');
    const email = form.querySelector('input[type="email"]')?.value.trim() || '';

    if (!EMAIL_PATTERN.test(email)) {
      message.textContent = 'Please enter a valid email address.';
      return;
    }
    if (form.classList.contains('is-sending')) return;

    form.classList.add('is-sending');
    message.textContent = '';
    try {
      await subscribe(email);
      // The subscription is what matters; tracking runs in the background.
      identifyAndResend(email).catch(() => {});
      try {
        window.localStorage.setItem(ALERTS_KEY, '1');
      } catch (error) {}
      form.classList.add('is-done');
      message.textContent = "You're in. We'll keep you posted on the pieces you've saved.";
    } catch (error) {
      message.textContent = 'Something went wrong, please try again.';
    } finally {
      form.classList.remove('is-sending');
    }
  }
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
if (!customElements.get('wishlist-grid')) customElements.define('wishlist-grid', WishlistGrid);
if (!customElements.get('saved-drawer-body')) customElements.define('saved-drawer-body', SavedDrawerBody);

// Keep every tab in step.
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
});
